import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import uuid from 'react-uuid';
import { DESC, VALUATION_DATE_KEY } from 'common/constants/analyst-dashboards';
import ColorSelector from 'dashboard409a/components/ColorSelector/ColorSelector';
import ModelsCalendar from 'dashboard409a/components/ModelsCalendar/ModelsCalendar';
import { ModelsTable } from 'dashboard409a/components/ModelsTable/ModelsTable';
import { ModelFilterType, modelsStatusOptions } from 'dashboard409a/components/ModelsTable/ModelsTable.types';
import { OperationsModal, useOperationsModal } from 'dashboard409a/components/OperationsModal';
import { GenericWorkboard, useNoteHandler } from 'dashboard409a/components/ValuationsTable/common';
import ViewToggle, { ViewType } from 'dashboard409a/components/ViewToggle/ViewToggle';
import { useModelsWithValuationData } from 'dashboard409a/hooks/useModelsWithValuationData';
import { useMultiColumnSort } from 'dashboard409a/hooks/useMultiColumnSort';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { ModelFilters } from 'dashboard409a/pages/Workboard/Filters';
import { CreateNoteRequest } from 'dashboard409a/services/attio.types';
import { RootStateType } from 'dashboard409a/states/store';
import { useLocalStorage } from 'services/hooks';
import { NormalizableDate, normalizeDate } from 'utilities/dateUtils';
import {
  ACTIVE_MODEL_VIEW_KEY,
  DEFAULT_VIEW_ID,
  MODELS_SORT_KEY,
  MODELS_VIEW_KEY,
  MODELS_VIEWS_KEY,
} from '../common/constants';
import { ModelSearchOption } from '../common/types';
import { ModelView, ModelViewsSelector } from '../ModelViewsSelector';

// Helper function to get or create Default view
const getDefaultView = (): ModelView => ({
  id: DEFAULT_VIEW_ID,
  name: 'Default',
  preparerIds: [],
  reviewerIds: [],
  searchStatus: [],
  fromDate: null,
  toDate: null,
  filterType: 'all',
});

// Helper function to ensure Default view exists in views array
const ensureDefaultView = (views: ModelView[]): ModelView[] => {
  // Normalize dates coming from localStorage (strings) into Date objects
  const normalizedViews = views.map(v => ({
    ...v,
    fromDate: normalizeDate((v as unknown as { fromDate?: unknown }).fromDate as NormalizableDate),
    toDate: normalizeDate((v as unknown as { toDate?: unknown }).toDate as NormalizableDate),
  }));

  const defaultView = normalizedViews.find(v => v.id === DEFAULT_VIEW_ID);
  if (!defaultView) {
    return [getDefaultView(), ...normalizedViews];
  }

  // Update Default view to ensure it has all required fields
  return normalizedViews.map(v =>
    v.id === DEFAULT_VIEW_ID
      ? {
        ...getDefaultView(),
        ...v,
        fromDate: (v as unknown as { fromDate?: unknown }).fromDate
          ? normalizeDate((v as unknown as { fromDate?: unknown }).fromDate as string | Date | null | undefined)
          : null,
        toDate: (v as unknown as { toDate?: unknown }).toDate
          ? normalizeDate((v as unknown as { toDate?: unknown }).toDate as string | Date | null | undefined)
          : null,
      }
      : v
  );
};

interface WorkboardModelsTableProps {
  // Filter type: 'current' for non-completed models, 'past' for completed models, 'unassigned' for models with no reviewers/preparers
  filterType?: ModelSearchOption;
  // Initial preparer user IDs
  preparerUserIds?: number[];
  // Initial reviewer user IDs
  reviewerUserIds?: number[];
}

const WorkboardModelsTable = ({
  filterType: initialFilterType = 'current',
  preparerUserIds: initialPreparerUserIds,
  reviewerUserIds: initialReviewerUserIds,
}: WorkboardModelsTableProps) => {
  // Use shared multi-column sort hook
  const { sortCriteria, orderBy, order, handleRequestSort } = useMultiColumnSort({
    storageKey: MODELS_SORT_KEY,
    defaultOrderBy: VALUATION_DATE_KEY,
    defaultOrder: DESC,
  });

  const [filterType, setFilterType] = useState<ModelSearchOption>(initialFilterType);
  const [preparerIds, setPreparerIds] = useState<number[]>(initialPreparerUserIds ?? []);
  const [reviewerIds, setReviewerIds] = useState<number[]>(initialReviewerUserIds ?? []);
  const [searchParam, setSearchParam] = useState('');
  const [searchStatus, setSearchStatus] = useState<string[]>([]);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [viewToApply, setViewToApply] = useState<ModelView | null>(null);
  const [currentViewId, setCurrentViewId] = useState<string | null>(DEFAULT_VIEW_ID);
  const [views, setViews] = useLocalStorage<ModelView[]>(MODELS_VIEWS_KEY, [getDefaultView()]);
  const [activeModelView] = useLocalStorage<ModelView | null>(ACTIVE_MODEL_VIEW_KEY, null);

  // Ensure Default view always exists and initialize with saved active view or default
  useEffect(() => {
    const updatedViews = ensureDefaultView(views);
    if (
      updatedViews.length !== views.length ||
      updatedViews.some(
        (v, i) =>
          v.id !== views[i]?.id || (v.id === DEFAULT_VIEW_ID && (v.fromDate === undefined || v.toDate === undefined))
      )
    ) {
      setViews(updatedViews);
    }

    // Check if we should load the saved active view
    let viewToLoad: ModelView | null = null;
    if (activeModelView) {
      // Check if the active view still exists in the views array
      const viewExists = updatedViews.find(v => v.id === activeModelView.id);
      if (viewExists) {
        viewToLoad = viewExists;
      }
    }

    // If no active view to load, use default view
    viewToLoad ??= updatedViews.find(v => v.id === DEFAULT_VIEW_ID) || getDefaultView();

    // Initialize with the selected view (active or default)
    if (!currentViewId || !viewToApply || currentViewId === DEFAULT_VIEW_ID) {
      setCurrentViewId(viewToLoad.id);
      setViewToApply(viewToLoad);
      setPreparerIds(viewToLoad.preparerIds);
      setReviewerIds(viewToLoad.reviewerIds);
      // Normalize searchStatus to always be an array
      let statusArray: string[] = [];
      if (Array.isArray(viewToLoad.searchStatus)) {
        statusArray = viewToLoad.searchStatus;
      } else if (viewToLoad.searchStatus) {
        statusArray = [viewToLoad.searchStatus];
      }
      setSearchStatus(statusArray);
      setFromDate(viewToLoad.fromDate);
      setToDate(viewToLoad.toDate);
      const filterTypeValue = viewToLoad.filterType || 'current';
      setFilterType(filterTypeValue === 'all' ? 'current' : (filterTypeValue as ModelSearchOption));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount
  const [storedView, setStoredView] = useLocalStorage<ViewType>(MODELS_VIEW_KEY, 'calendar');
  const view: ViewType = storedView === 'calendar' || storedView === 'table' ? storedView : 'calendar';
  const setView = (newView: ViewType) => {
    setStoredView(newView);
  };

  const currentUserId = useSelector((state: RootStateType) => state.user.id);

  const {
    rows: allModels,
    isLoading,
    updateModelNote,
    updateModelStatus,
    updateModelDueDate,
  } = useModelsWithValuationData({
    filterType,
    preparerUserIds: preparerIds,
    reviewerUserIds: reviewerIds,
    orderBy,
    order,
    sortCriteria,
  });

  const models = useMemo(() => {
    let filtered = [...allModels];

    if (searchParam.trim()) {
      const searchLower = searchParam.toLowerCase().trim();
      filtered = filtered.filter(model => model.company_name?.toLowerCase().includes(searchLower));
    }

    if (searchStatus && Array.isArray(searchStatus) && searchStatus.length > 0) {
      // If "All" is selected, don't filter by status (show all statuses)
      const hasAll = searchStatus.includes('All');
      if (!hasAll) {
        const statusValues = searchStatus.filter(s => s !== '');
        if (statusValues.length > 0) {
          const targetStatuses = statusValues
            .map(status => modelStatusMap[status as keyof typeof modelStatusMap])
            .filter((status): status is number => status !== undefined);
          if (targetStatuses.length > 0) {
            filtered = filtered.filter(model => targetStatuses.includes(model?.model_status ?? 0));
          }
        }
      }
      // If "All" is selected, we don't filter by status, so all models pass through
    }

    if (fromDate) {
      filtered = filtered.filter(model => {
        if (!model.supporting_due_date) {
          return false;
        }
        const modelDate = new Date(model.supporting_due_date);
        return modelDate >= fromDate;
      });
    }

    if (toDate) {
      filtered = filtered.filter(model => {
        if (!model.supporting_due_date) {
          return false;
        }
        const modelDate = new Date(model.supporting_due_date);
        const toDateEnd = new Date(toDate);
        toDateEnd.setHours(23, 59, 59, 999);
        return modelDate <= toDateEnd;
      });
    }

    return filtered;
  }, [allModels, searchParam, searchStatus, fromDate, toDate]);

  const {
    isNoteLoading,
    noteError,
    handleNoteSave: handleNoteSaveGeneric,
  } = useNoteHandler({
    onSuccess: (id, noteContent, createdNote) => {
      if (createdNote?.fulfillment) {
        const fulfillmentId = createdNote.fulfillment;
        const noteId = createdNote.id;
        updateModelNote(fulfillmentId, noteContent, noteId);
      } else {
        updateModelNote(id as number, noteContent);
      }
    },
  });

  const handleNoteSave = async (fulfillment: number, noteContent: string, currentNote?: string) => {
    if (!fulfillment) {
      throw new Error('Invalid fulfillment ID. Cannot save note.');
    }

    const request: CreateNoteRequest = {
      content: noteContent,
      fulfillment,
      reference_type: 'fu',
    };

    const currentModel = models.find(m => m.id === fulfillment);
    const hasExistingNote = Boolean(
      (currentNote && currentNote.trim() !== '' && currentNote !== '-') || currentModel?.note?.content?.trim() !== ''
    );

    let existingNoteId: string | undefined;

    if (hasExistingNote) {
      const noteId = currentModel?.note?.id;
      if (noteId && noteId !== 0) {
        existingNoteId = noteId.toString();
      } else {
        existingNoteId = undefined;
      }
    }

    await handleNoteSaveGeneric(fulfillment, noteContent, currentNote, request, hasExistingNote, existingNoteId);
  };

  const handleStatusUpdate = (modelId: number, newStatus: number) => {
    updateModelStatus(modelId, newStatus);
  };

  const handleFiltersChange = useCallback((filters: {
    searchParam: string;
    searchStatus: string[];
    fromDate: Date | null;
    toDate: Date | null;
    preparerIds: number[];
    reviewerIds: number[];
    filterType?: ModelFilterType;
  }) => {
    setPreparerIds(filters.preparerIds);
    setReviewerIds(filters.reviewerIds);
    setSearchParam(filters.searchParam);
    setSearchStatus(filters.searchStatus);
    setFromDate(filters.fromDate);
    setToDate(filters.toDate);
    if (filters.filterType) {
      const filterTypeValue = filters.filterType === 'all' ? 'current' : filters.filterType;
      setFilterType(filterTypeValue as ModelSearchOption);
    }
  }, []);

  const statusOptionsLoading = false;
  const stableOnFiltersChange = useMemo(() => handleFiltersChange, [handleFiltersChange]);

  const { openModal, selectedModel, handleOpenModal, handleCloseModal } = useOperationsModal({ models });

  const handleEventDrop = async (modelId: number, newDueDate: string) => {
    updateModelDueDate(modelId, newDueDate);
  };

  const handleLoadView = useCallback((savedView: ModelView) => {
    setPreparerIds(savedView.preparerIds);
    setReviewerIds(savedView.reviewerIds);
    let statusArray: string[] = [];
    if (Array.isArray(savedView.searchStatus)) {
      statusArray = savedView.searchStatus;
    } else if (savedView.searchStatus) {
      statusArray = [savedView.searchStatus];
    }
    setSearchStatus(statusArray);
    setFromDate(savedView.fromDate);
    setToDate(savedView.toDate);
    const filterTypeValue = savedView.filterType || 'current';
    setFilterType(filterTypeValue === 'all' ? 'current' : (filterTypeValue as ModelSearchOption));
    setViewToApply({ ...savedView });
    setCurrentViewId(savedView.id);
  }, []);

  const handleViewChange = useCallback(
    (selectedView: ModelView | null) => {
      if (selectedView) {
        handleLoadView(selectedView);
      } else {
        // When null, load Default view
        const defaultView = views.find(v => v.id === DEFAULT_VIEW_ID) || getDefaultView();
        handleLoadView(defaultView);
      }
    },
    [handleLoadView, views]
  );

  const handleSaveView = useCallback(
    (viewName: string) => {
      const newView: ModelView = {
        id: uuid(),
        name: viewName.trim(),
        preparerIds: [...preparerIds],
        reviewerIds: [...reviewerIds],
        searchStatus,
        fromDate,
        toDate,
        filterType,
      };
      setViews(prev => {
        const updated = ensureDefaultView(prev);
        return [...updated, newView];
      });
      setCurrentViewId(newView.id);
      setViewToApply(newView);
    },
    [preparerIds, reviewerIds, searchStatus, fromDate, toDate, filterType, setViews]
  );

  const handleDeleteView = useCallback(
    (viewId: string) => {
      if (viewId === DEFAULT_VIEW_ID) {
        return; // Cannot delete Default view
      }
      setViews(prev => {
        const filtered = prev.filter(v => v.id !== viewId);
        return ensureDefaultView(filtered);
      });
      if (currentViewId === viewId) {
        const defaultView = getDefaultView();
        setCurrentViewId(DEFAULT_VIEW_ID);
        setViewToApply(defaultView);
        handleLoadView(defaultView);
      }
    },
    [currentViewId, setViews, handleLoadView]
  );

  const handleEditView = useCallback(
    (viewToEdit: ModelView) => {
      const updatedView: ModelView = {
        ...viewToEdit,
        preparerIds: [...preparerIds],
        reviewerIds: [...reviewerIds],
        searchStatus,
        fromDate,
        toDate,
        filterType,
      };
      setViews(prev => {
        const updated = prev.map(v => (v.id === viewToEdit.id ? updatedView : v));
        return ensureDefaultView(updated);
      });
      if (currentViewId === viewToEdit.id) {
        setViewToApply(updatedView);
        // Don't call handleLoadView here as it would cause a loop - the filters are already updated
      }
    },
    [preparerIds, reviewerIds, searchStatus, fromDate, toDate, filterType, currentViewId, setViews]
  );

  return (
    <GenericWorkboard>
      <Box sx={{ display: 'flex', alignItems: 'center', m: 2, pr: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, borderRight: '1px solid #9a9696', pr: 2 }}>
          <ViewToggle view={view} onChange={setView} />
          <ColorSelector />
          <ModelFilters
            statusOptions={modelsStatusOptions}
            statusOptionsLoading={statusOptionsLoading}
            onFiltersChange={stableOnFiltersChange}
            viewToApply={viewToApply}
            views={views}
            currentViewId={currentViewId}
            onViewChange={handleViewChange}
            onSaveView={handleSaveView}
            onDeleteView={handleDeleteView}
            onEditView={handleEditView}
          />
        </Box>
        <ModelViewsSelector onLoadView={handleLoadView} currentViewId={currentViewId} views={views} />
        <Box marginRight={2}>
          <Typography variant="body1">Total models: {models.length.toLocaleString()}</Typography>
        </Box>
      </Box>
      {view === 'calendar' ? (
        <ModelsCalendar
          models={models}
          isLoading={isLoading}
          onOpenPreview={handleOpenModal}
          onEventDrop={handleEventDrop}
          userId={currentUserId?.toString() || ''}
        />
      ) : (
        <ModelsTable
          isLoading={isLoading}
          rows={models}
          onNoteSave={handleNoteSave}
          orderBy={orderBy}
          order={order}
          sortCriteria={sortCriteria}
          onRequestSort={handleRequestSort}
          onStatusUpdate={handleStatusUpdate}
          isNoteLoading={isNoteLoading}
          noteError={noteError}
          onRowClick={handleOpenModal}
        />
      )}

      <OperationsModal
        open={openModal}
        onClose={handleCloseModal}
        data={selectedModel}
        isLoading={isLoading}
        isFromModels
      />
    </GenericWorkboard>
  );
};

export default WorkboardModelsTable;
